import { useState, useCallback } from "react";
import { FormErrors, SignupFormData } from "../types/authTypes";
import { validateEmail, validateDob, validatePhone } from "../utils/validation";

export const useSignupForm = () => {
  // ---------- State ----------
  const [formData, setFormData] = useState<SignupFormData>({
    user_name: "",
    name: "",
    organization_name: "",
    org_Arabic_Name: "",
    business_location: "",
    business_type: "",
    date_of_birth: "",
    phone_number: "",
    address_line_1: "",
    email_id: "",
    state: "",
    postal_code: "",
    country: "",
    time_zone_id: "",
    role: "Owner",
    filename: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [inputValue, setInputValue] = useState<string>("");

  // ---------- Helpers ----------
  const updateField = useCallback((field: keyof SignupFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setError = useCallback((field: keyof FormErrors, message?: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  }, []);

  const clearError = useCallback((field: keyof FormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  // ---------- Field Handlers ----------
  const handleEmailChange = useCallback(
    (value: string) => {
      const isValid = validateEmail(value);
      updateField("email_id", value);
      setError("email_id", isValid ? undefined : "Please enter a valid email address");
    },
    [updateField, setError]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const { numericValue, errors: phoneErrors } = validatePhone(value);
      updateField("phone_number", numericValue);
      setErrors((prev) => ({ ...prev, ...phoneErrors }));
    },
    [updateField]
  );

  const handleDobChange = useCallback(
    (value: string) => {
      const { formattedValue, date, errors: dobErrors } = validateDob(value);
      updateField("date_of_birth", date);
      setInputValue(formattedValue);
      setErrors((prev) => ({ ...prev, ...dobErrors }));
    },
    [updateField]
  );

  // ---------- Return ----------
  return {
    formData,
    errors,
    inputValue,
    updateField,
    handleEmailChange,
    handleDobChange,
    handlePhoneChange,
    setError,
    clearError,
    clearAllErrors,
  };
};
